import { useEffect, useState } from 'preact/hooks'
import type { FactureDemo } from '../../serveur/src/comptabilite/factures.ts'

const argent = (cents: number) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100)
const demain = () => new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10)

interface ListeFactures { factures: FactureDemo[]; total_a_recevoir_cents: number; tronque: boolean; limites: string[] }
interface SerieFacturable { id: string; nom_ressource: string; locataire: { nom: string }; montant_total_cents: number; etat?: string }
interface Lot { id: string; format: string; periode: { du: string; au: string; environnement: string }; sha256: string; cree_le_utc: string }
interface Poste { compte_code: string; compte_libelle: string; debit_cents: number; credit_cents: number; transactions: number }

export function FinancesDocumentsPane({ baseUrl, vue, du, au, environnement, permissions, onAuth, onOuvrirDemande }: {
  baseUrl: string
  vue: 'factures' | 'lots'
  du: string
  au: string
  environnement: string
  permissions: string[]
  onAuth?: () => void
  onOuvrirDemande?: (id: string) => void
}) {
  const [factures, setFactures] = useState<ListeFactures | null>(null)
  const [series, setSeries] = useState<SerieFacturable[]>([])
  const [lots, setLots] = useState<Lot[]>([])
  const [postes, setPostes] = useState<Poste[]>([])
  const [serieId, setSerieId] = useState('')
  const [echeance, setEcheance] = useState(demain)
  const [detailId, setDetailId] = useState('')
  const [charge, setCharge] = useState(true)
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState('')
  const [avis, setAvis] = useState('')
  const peutModifier = permissions.includes('*') || permissions.includes('finances.modifier')
  const peutExporter = permissions.includes('*') || permissions.includes('finances.exporter')
  const api = async (route: string, options: RequestInit = {}) => {
    const rep = await fetch(`${baseUrl}${route}`, { ...options, credentials: 'same-origin', headers: { Accept: 'application/json', ...(options.body ? { 'content-type': 'application/json' } : {}), ...options.headers } })
    if (rep.status === 401) onAuth?.()
    if (!rep.ok) {
      const corps = await rep.json().catch(() => null)
      throw new Error(corps?.erreur?.message ?? 'La requête financière a échoué.')
    }
    return rep
  }
  const rafraichir = async () => {
    setCharge(true)
    setErreur('')
    try {
      if (vue === 'factures') {
        const [f, s] = await Promise.all([
          api('/api/admin/finances/factures').then((r) => r.json() as Promise<ListeFactures>),
          api('/api/admin/demandes/series').then((r) => r.json() as Promise<{ series: SerieFacturable[] }>).catch(() => ({ series: [] })),
        ])
        setFactures(f)
        setSeries(s.series.filter((serie) => !serie.etat || serie.etat === 'confirmee'))
      } else {
        const params = new URLSearchParams({ du, au, environnement })
        const [l, p] = await Promise.all([
          api('/api/admin/finances/lots').then((r) => r.json() as Promise<{ lots: Lot[] }>),
          api(`/api/admin/finances/postes?${params}`).then((r) => r.json() as Promise<{ postes: Poste[] }>),
        ])
        setLots(l.lots)
        setPostes(p.postes)
      }
    } catch (e) { setErreur(e instanceof Error ? e.message : 'Chargement impossible.') }
    finally { setCharge(false) }
  }
  useEffect(() => { void rafraichir() }, [baseUrl, vue, du, au, environnement])
  const emettre = async () => {
    if (!serieId) return
    setOccupe(true); setErreur(''); setAvis('')
    try {
      const rep = await api(`/api/admin/finances/factures/demo/series/${encodeURIComponent(serieId)}`, {
        method: 'POST', body: JSON.stringify({ echeance, confirmation: 'DEMONSTRATION' }),
      })
      const resultat = await rep.json()
      setAvis(`${resultat.facture.numero} créé ou retrouvé. Il s’agit d’une pièce de démonstration, sans validité fiscale.`)
      await rafraichir()
    } catch (e) { setErreur(e instanceof Error ? e.message : 'Émission impossible.') }
    finally { setOccupe(false) }
  }
  const envoyerFacture = async (facture: FactureDemo) => {
    setOccupe(true); setErreur(''); setAvis('')
    try {
      await api(`/api/admin/finances/factures/${encodeURIComponent(facture.id)}/envoyer-demo`, {
        method: 'POST', body: JSON.stringify({ cle_idempotence: crypto.randomUUID().replace(/-/g, '') }),
      })
      setAvis(`La pièce ${facture.numero} a été placée dans la boîte d’envoi simulée. Aucun courriel réel n’a été expédié.`)
    } catch (e) { setErreur(e instanceof Error ? e.message : 'Envoi simulé impossible.') }
    finally { setOccupe(false) }
  }
  const telecharger = async (lot: Lot) => {
    setOccupe(true); setErreur('')
    try {
      const rep = await api(`/api/admin/finances/lots/${encodeURIComponent(lot.id)}.csv`)
      const url = URL.createObjectURL(await rep.blob())
      const lien = document.createElement('a')
      lien.href = url; lien.download = `funo-lot-${lot.id}.csv`; lien.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) { setErreur(e instanceof Error ? e.message : 'Téléchargement impossible.') }
    finally { setOccupe(false) }
  }
  return <>
    {erreur && <div class="finance-alert finance-alert-error" role="alert">{erreur}</div>}
    {avis && <div class="finance-alert" role="status">{avis}</div>}
    {vue === 'factures' ? <>
      <div class="finance-kpis finance-suivi-kpis">
        <article><span>Pièces de démonstration</span><strong>{factures?.factures.length ?? '—'}</strong></article>
        <article><span>Solde opérationnel de démonstration</span><strong>{argent(factures?.total_a_recevoir_cents ?? 0)}</strong></article>
      </div>
      <div class="finance-card finance-suivi-note"><div class="finance-card-head"><h3>Factures de démonstration</h3></div>
        <p>Numéros DEMO uniquement : ces documents ne sont pas des factures fiscales. Leurs soldes déduisent seulement les encaissements simulés confirmés. Les taxes, la numérotation officielle et les échéances doivent être validées avant un usage réel.</p>
      </div>
      {peutModifier && <div class="finance-card"><div class="finance-card-head"><h3>Préparer une pièce pour une série</h3></div>
        <div class="finance-toolbar">
          <label>Série<select value={serieId} onChange={(e) => setSerieId(e.currentTarget.value)}><option value="">Choisir une série</option>{series.map((s) => <option value={s.id}>{s.locataire.nom} · {s.nom_ressource} · {argent(s.montant_total_cents)}</option>)}</select></label>
          <label>Échéance indicative<input type="date" value={echeance} onChange={(e) => setEcheance(e.currentTarget.value)} /></label>
          <button class="finance-button finance-primary" disabled={!serieId || occupe || charge} onClick={emettre}>Créer la pièce DEMO</button>
        </div>
        <p class="finance-inline-note">Chaque date de la série apparaît sur sa propre ligne. Le paiement et la signature restent deux étapes distinctes.</p>
      </div>}
      <div class="finance-card"><div class="finance-card-head"><h3>Liste des pièces</h3></div>
        {charge ? <div class="finance-empty">Chargement…</div> : !factures?.factures.length ? <div class="finance-empty">Aucune pièce de démonstration enregistrée.</div> : <div class="finance-scroll"><table><thead><tr><th>Numéro</th><th>Locataire</th><th>Émise / échéance</th><th>État</th><th class="finance-number">Total</th><th class="finance-number">Solde</th><th>Document</th></tr></thead><tbody>
          {factures.factures.map((f) => <>
            <tr key={f.id}><td><strong>{f.numero}</strong><small>{f.lignes.length} ligne(s) · {f.service_cle}</small><button class="finance-link" type="button" aria-expanded={detailId === f.id} onClick={() => setDetailId(detailId === f.id ? '' : f.id)}>{detailId === f.id ? 'Masquer les dates' : 'Voir les dates et frais'}</button></td><td>{f.client.organisation || f.client.nom}<small>{f.client.courriel}</small>{f.source.type === 'demande' && onOuvrirDemande ? <button class="finance-link" onClick={() => onOuvrirDemande(f.source.id)}>Voir la demande</button> : null}</td><td>{f.emise_le}<small>Échéance {f.echeance}</small></td><td>{f.etat === 'reglee' ? 'Réglée' : f.etat === 'partiellement_reglee' ? 'Partielle' : 'À recevoir'}</td><td class="finance-number">{argent(f.total_cents)}</td><td class="finance-number">{argent(f.solde_cents)}</td><td><a class="finance-link" href={`${baseUrl}/api/admin/finances/factures/${encodeURIComponent(f.id)}.pdf`} download={`${f.numero}.pdf`}>PDF</a>{peutModifier && <button class="finance-link" disabled={occupe} onClick={() => void envoyerFacture(f)}>Envoyer (simulé)</button>}</td></tr>
            {detailId === f.id && <tr key={`${f.id}:detail`}><td colSpan={7}><ul>{f.lignes.map((l, i) => <li key={i}>{l.date_locale ? `${l.date_locale} · ` : ''}{l.salle ? `${l.salle} · ` : ''}{l.libelle} — {argent(l.montant_cents)}</li>)}</ul><small>{f.mention}</small></td></tr>}
          </>)}
        </tbody></table></div>}
      </div>
      {factures?.tronque && <p class="finance-footnote">Liste limitée à 500 pièces; le total ne couvre que les pièces visibles.</p>}
    </> : <>
      <div class="finance-card finance-suivi-note"><div class="finance-card-head"><h3>Lots comptables et PG Solutions</h3></div>
        <p>Funo conserve les exports CSV exacts et leur empreinte SHA-256. Le format actuel est générique; aucun import PG Solutions n’a été certifié. La municipalité doit fournir son modèle d’import, sa charte et les codes de postes avant le pont final.</p>
      </div>
      <div class="finance-card"><div class="finance-card-head"><h3>Transactions par poste comptable</h3></div>
        {charge ? <div class="finance-empty">Chargement…</div> : !postes.length ? <div class="finance-empty">Aucune écriture ventilée sur la période et dans ce mode.</div> : <div class="finance-scroll"><table><thead><tr><th>Poste</th><th>Libellé</th><th>Transactions</th><th class="finance-number">Débit</th><th class="finance-number">Crédit</th></tr></thead><tbody>{postes.map((p) => <tr key={p.compte_code}><td><strong>{p.compte_code}</strong></td><td>{p.compte_libelle}</td><td>{p.transactions}</td><td class="finance-number">{argent(p.debit_cents)}</td><td class="finance-number">{argent(p.credit_cents)}</td></tr>)}</tbody></table></div>}
      </div>
      <div class="finance-card"><div class="finance-card-head"><h3>Lots CSV historisés</h3></div>
        {charge ? <div class="finance-empty">Chargement…</div> : !lots.length ? <div class="finance-empty">Aucun lot exporté. Créez-en un depuis l’onglet Rapport.</div> : <div class="finance-scroll"><table><thead><tr><th>Créé le</th><th>Format</th><th>Période</th><th>Empreinte SHA-256</th><th></th></tr></thead><tbody>{lots.map((l) => <tr key={l.id}><td>{l.cree_le_utc.slice(0, 16).replace('T', ' ')}</td><td>{l.format}</td><td>{l.periode.du} au {l.periode.au}<small>{l.periode.environnement}</small></td><td><code title={l.sha256}>{l.sha256.slice(0, 16)}…</code></td><td>{peutExporter && <button class="finance-link" disabled={occupe} onClick={() => telecharger(l)}>Télécharger</button>}</td></tr>)}</tbody></table></div>}
      </div>
    </>}
  </>
}
